import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import SplashScreen from './components/SplashScreen';
import './App.css';

import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import NewDashboard from './pages/NewDashboard';
import Vocabulary from './pages/Vocabulary';
import Lessons from './pages/Lessons';
import Quiz from './pages/Quiz';
import Chat from './pages/Chat';
import Admin from './pages/Admin';
import Situations from './pages/Situations';
import Grammar from './pages/Grammar';
import DarijaSounds from './pages/DarijaSounds';
import LevelPage from './pages/LevelPage';
import CategoryQuiz from './pages/CategoryQuiz';
import GrandQuiz from './pages/GrandQuiz';
import Settings from './pages/Settings';
import Premium from './pages/Premium';
import PaymentSuccess from './pages/PaymentSuccess';
import Review from './pages/Review';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ============ KEEP-ALIVE SYSTEM ============
// Keeps server awake with background pings - NO blocking loading screen
const KeepAliveProvider = ({ children }) => {
  useEffect(() => {
    const wakeUpServer = async () => {
      try {
        await fetch(`${API}/stats`, { method: 'GET' });
        console.log('[KeepAlive] Server is active');
      } catch (error) {
        console.log('[KeepAlive] Server waking up...');
      }
    };
    
    // Wake up server immediately
    wakeUpServer();
    
    // Keep server alive with ping every 4 minutes
    const interval = setInterval(wakeUpServer, 240000);
    
    return () => clearInterval(interval);
  }, []);
  
  // NO blocking loading screen - app loads immediately
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">
          {language === 'en' ? 'Loading...' : 'Chargement...'}
        </p>
      </div>
    );
  }
  
  // GUEST MODE: Allow access without authentication for testing
  // Users can use the app without signing in
  return children;
};

// Router wrapper that checks for OAuth callback
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL hash for session_id (OAuth callback) - CRITICAL: Check synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <NewDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sounds"
        element={
          <ProtectedRoute>
            <DarijaSounds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <Review />
          </ProtectedRoute>
        }
      />
      <Route
        path="/level/:levelId"
        element={
          <ProtectedRoute>
            <LevelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/category-quiz"
        element={
          <ProtectedRoute>
            <CategoryQuiz />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grand-quiz"
        element={
          <ProtectedRoute>
            <GrandQuiz />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vocabulary"
        element={
          <ProtectedRoute>
            <Vocabulary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lessons"
        element={
          <ProtectedRoute>
            <Lessons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quiz"
        element={
          <ProtectedRoute>
            <Quiz />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/situations"
        element={
          <ProtectedRoute>
            <Situations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grammar"
        element={
          <ProtectedRoute>
            <Grammar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Vocabulary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium"
        element={
          <ProtectedRoute>
            <Premium />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const AppContent = () => {
  const { isLanguageSelected } = useLanguage();

  // Show Splash Screen if language not selected
  if (!isLanguageSelected()) {
    return <SplashScreen />;
  }

  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
};

function App() {
  return (
    <KeepAliveProvider>
      <LanguageProvider>
        <AuthProvider>
          <PreferencesProvider>
            <AppContent />
            <Toaster position="top-center" richColors />
          </PreferencesProvider>
        </AuthProvider>
      </LanguageProvider>
    </KeepAliveProvider>
  );
}

export default App;
